import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
    getOnboardingStatus, 
    completeOnboardingStep, 
    skipOnboarding,
    ONBOARDING_STEPS,
    isStepCompleted,
    isOnboardingComplete
} from '../api/onboardingAPI';
import { log } from '../utils/logger';

// Create the onboarding context
const OnboardingContext = createContext();

// Provider component
export const OnboardingProvider = ({ children, userEmail }) => {
    const [onboardingStatus, setOnboardingStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentStep, setCurrentStep] = useState(null);

    // Load onboarding status on mount and when userEmail changes
    useEffect(() => {
        if (userEmail) {
            // Check if onboarding was reset before loading
            const wasReset = sessionStorage.getItem('onboardingReset');
            if (wasReset) {
                sessionStorage.removeItem('onboardingReset');
            }
            loadOnboardingStatus();
        }
    }, [userEmail]);

    // Listen for page visibility changes to reload after potential reset
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && userEmail) {
                // Check if onboarding was reset while user was away
                const wasReset = sessionStorage.getItem('onboardingReset');
                if (wasReset) {
                    sessionStorage.removeItem('onboardingReset');
                    forceReload();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [userEmail]);

    const loadOnboardingStatus = async () => {
        try {
            setLoading(true);
            setError(null);
            const status = await getOnboardingStatus(userEmail);
            setOnboardingStatus(status);
            setCurrentStep(status?.current_step || 1);
            log('Onboarding status loaded:', status);
        } catch (err) {
            setError(err.message);
            console.error('Failed to load onboarding status:', err);
        } finally {
            setLoading(false);
        }
    };

    const completeStep = async (stepNumber) => {
        try {
            setLoading(true);
            setError(null);
            const updatedStatus = await completeOnboardingStep(userEmail, stepNumber);
            setOnboardingStatus(updatedStatus);
            setCurrentStep(updatedStatus?.current_step || stepNumber + 1);
            log(`Step ${stepNumber} completed, new status:`, updatedStatus);
            return updatedStatus;
        } catch (err) {
            setError(err.message);
            console.error(`Failed to complete step ${stepNumber}:`, err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const skipWalkthrough = async () => {
        try {
            setLoading(true);
            setError(null);
            const updatedStatus = await skipOnboarding(userEmail);
            setOnboardingStatus(updatedStatus);
            setCurrentStep(null);
            log('Onboarding skipped:', updatedStatus);
            return updatedStatus;
        } catch (err) {
            setError(err.message);
            console.error('Failed to skip onboarding:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Helper functions
    const isStepComplete = (stepNumber) => {
        return isStepCompleted(onboardingStatus, stepNumber);
    };

    const isComplete = () => {
        // Don't consider complete if status hasn't loaded yet
        if (!onboardingStatus) {
            return false;
        }
        const result = isOnboardingComplete(onboardingStatus);
        return result;
    };

    const shouldShowStep = (stepNumber) => {
        // Show step if:
        // 1. Onboarding status has loaded (not null)
        // 2. Onboarding is not complete/skipped
        // 3. Current step matches the step number
        // 4. Previous steps are completed (if any)
        
        // Don't show anything while onboarding status is loading
        if (onboardingStatus === null) {
            return false;
        }
        
        if (isComplete()) {
            return false;
        }
        
        // Default to step 1 if currentStep is null
        const effectiveCurrentStep = currentStep || 1;
        if (effectiveCurrentStep !== stepNumber) {
            return false;
        }
        
        // Check if all previous steps are completed
        for (let i = 1; i < stepNumber; i++) {
            if (!isStepComplete(i)) {
                return false;
            }
        }
        
        return true;
    };

    const getNextStep = () => {
        if (isComplete()) return null;
        
        // Find the first incomplete step
        for (let i = 1; i <= 6; i++) {
            if (!isStepComplete(i)) {
                return i;
            }
        }
        
        return null;
    };

    // Force reload onboarding status (useful after reset)
    const forceReload = async () => {
        setOnboardingStatus(null);
        setCurrentStep(null);
        setError(null);
        if (userEmail) {
            await loadOnboardingStatus();
        }
    };

    const value = {
        // State
        onboardingStatus,
        loading,
        error,
        currentStep,
        
        // Actions
        loadOnboardingStatus,
        completeStep,
        skipWalkthrough,
        forceReload,
        
        // Helper functions
        isStepComplete,
        isComplete,
        shouldShowStep,
        getNextStep,
        
        // Constants
        STEPS: ONBOARDING_STEPS
    };

    return (
        <OnboardingContext.Provider value={value}>
            {children}
        </OnboardingContext.Provider>
    );
};

// Hook to use the onboarding context
export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error('useOnboarding must be used within an OnboardingProvider');
    }
    return context;
};

// Higher-order component to wrap components that need onboarding
export const withOnboarding = (Component) => {
    return (props) => {
        const onboarding = useOnboarding();
        return <Component {...props} onboarding={onboarding} />;
    };
}; 